/**
 * Notifications Service
 * Polls backend APIs and triggers notifications in the notificationsStore
 *
 * Features:
 * - New email notifications (30s polling)
 * - New candidate notifications (60s polling)
 * - 15-minute appointment reminders (60s polling)
 * - Chat message notifications (when implemented)
 */

import emailsService, { Email } from './emails.service';
import apiService from './api.service';
import appointmentsService from './appointments.service';
import { useNotificationsStore } from '../stores/notificationsStore';

interface NotificationTracker {
  lastEmailCheck: string | null;
  lastCandidateCheck: string | null;
  notifiedAppointments: Set<string>;
}

class NotificationsService {
  private emailPolling: NodeJS.Timeout | null = null;
  private candidatePolling: NodeJS.Timeout | null = null;
  private appointmentPolling: NodeJS.Timeout | null = null;

  private tracker: NotificationTracker = {
    lastEmailCheck: null,
    lastCandidateCheck: null,
    notifiedAppointments: new Set<string>()
  };

  private isRunning = false;

  constructor() {
    this.loadTracker();
  }

  /**
   * Load notification tracker from localStorage
   */
  private loadTracker(): void {
    try {
      const stored = localStorage.getItem('notification_tracker');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.tracker = {
          ...parsed,
          notifiedAppointments: new Set(parsed.notifiedAppointments || [])
        };
      }
    } catch (error) {
      console.warn('⚠️ Could not load notification tracker:', error);
    }
  }

  /**
   * Save notification tracker to localStorage
   */
  private saveTracker(): void {
    try {
      const toSave = {
        ...this.tracker,
        notifiedAppointments: Array.from(this.tracker.notifiedAppointments)
      };
      localStorage.setItem('notification_tracker', JSON.stringify(toSave));
    } catch (error) {
      console.warn('⚠️ Could not save notification tracker:', error);
    }
  }

  /**
   * Start all polling intervals
   */
  startPolling(): void {
    if (this.isRunning) {
      console.log('🔔 Notification service already running');
      return;
    }

    console.log('🔔 Starting notification service...');
    this.isRunning = true;

    // Nur pollen, wenn der Tab sichtbar ist (Page Visibility API).
    // Spart bei Nutzern mit Hintergrund-Tabs einen Großteil der Server-Last.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    if (typeof document === 'undefined' || !document.hidden) {
      this.resumeIntervals();
    }
  }

  private handleVisibilityChange = (): void => {
    if (!this.isRunning) return;
    if (document.hidden) {
      this.clearIntervals();
    } else {
      this.resumeIntervals();
    }
  };

  private resumeIntervals(): void {
    if (this.emailPolling) return; // läuft bereits
    // Sofort einmal prüfen (frische Daten nach Rückkehr in den Tab)
    this.checkNewEmails();
    this.checkNewCandidates();
    this.check15MinReminders();

    this.emailPolling = setInterval(() => this.checkNewEmails(), 30000);
    this.candidatePolling = setInterval(() => this.checkNewCandidates(), 60000);
    this.appointmentPolling = setInterval(() => this.check15MinReminders(), 60000);
    console.log('✅ Notification polling aktiv (Tab sichtbar)');
  }

  private clearIntervals(): void {
    if (this.emailPolling) { clearInterval(this.emailPolling); this.emailPolling = null; }
    if (this.candidatePolling) { clearInterval(this.candidatePolling); this.candidatePolling = null; }
    if (this.appointmentPolling) { clearInterval(this.appointmentPolling); this.appointmentPolling = null; }
  }

  /**
   * Stop all polling intervals
   */
  stopPolling(): void {
    if (!this.isRunning) {
      return;
    }

    console.log('🛑 Stopping notification service...');

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.clearIntervals();

    this.isRunning = false;
    this.saveTracker();
    console.log('✅ Notification service stopped');
  }

  /**
   * Check for new emails
   * Triggers notification for unread emails received after last check
   */
  async checkNewEmails(): Promise<void> {
    try {
      const response = await emailsService.getAll(1, 10, 'inbox');

      if (!response.success || !response.data) {
        return;
      }

      const now = new Date().toISOString();
      const lastCheck = this.tracker.lastEmailCheck
        ? new Date(this.tracker.lastEmailCheck)
        : new Date(Date.now() - 60000); // 1 minute ago on first run

      // Find new emails (inbound only)
      const newEmails = response.data.filter((email: Email) => {
        const emailDate = new Date(email.created_at);
        return (
          email.direction === 'inbound' &&
          emailDate > lastCheck &&
          !email.opened_at // Unread
        );
      });

      // Trigger notifications for new emails
      newEmails.forEach((email: Email) => {
        const sender = email.from_name || email.from_email;
        const subject = email.subject || '(Kein Betreff)';

        useNotificationsStore.getState().notifyEmailReceived(sender, subject);
      });

      if (newEmails.length > 0) {
        console.log(`📧 ${newEmails.length} neue E-Mail(s)`);
      }

      this.tracker.lastEmailCheck = now;
      this.saveTracker();

    } catch (error) {
      console.error('❌ Error checking new emails:', error);
    }
  }

  /**
   * Check for new candidates
   * Triggers notification for recently added candidates
   */
  async checkNewCandidates(): Promise<void> {
    try {
      const response = await apiService.getCandidates({ limit: 20, offset: 0 });

      if (!response.success || !response.data) {
        return;
      }

      const now = new Date().toISOString();
      const lastCheck = this.tracker.lastCandidateCheck
        ? new Date(this.tracker.lastCandidateCheck)
        : new Date(Date.now() - 60000); // 1 minute ago on first run

      // Find new candidates
      const newCandidates = Array.isArray(response.data) ? response.data.filter((candidate: any) => {
        const createdDate = candidate.created_at ? new Date(candidate.created_at) : null;
        return createdDate && createdDate > lastCheck;
      }) : [];

      // Trigger notifications for new candidates
      newCandidates.forEach((candidate: any) => {
        const name = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Neuer Kandidat';
        const position = candidate.current_job_title || 'Position unbekannt';
        const matchScore = candidate.match_score || 0;

        useNotificationsStore.getState().notifyNewCandidate(name, position, matchScore);
      });

      if (newCandidates.length > 0) {
        console.log(`👤 ${newCandidates.length} neue(r) Kandidat(en)`);
      }

      this.tracker.lastCandidateCheck = now;
      this.saveTracker();

    } catch (error) {
      console.error('❌ Error checking new candidates:', error);
    }
  }

  /**
   * Check for appointments starting in 15 minutes
   * USER REQUIREMENT: "15 vorher erinnerungen von terminen"
   */
  async check15MinReminders(): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await appointmentsService.getByDate(today);

      if (!response.success || !response.data) {
        return;
      }

      const now = new Date();
      const in15Min = new Date(now.getTime() + 15 * 60000); // 15 minutes from now
      const in16Min = new Date(now.getTime() + 16 * 60000); // 16 minutes from now (window)

      response.data.forEach((appointment: any) => {
        // Skip if already notified
        if (this.tracker.notifiedAppointments.has(appointment.id)) {
          return;
        }

        // Parse appointment start time - safely handle undefined/null
        // Check all possible field names for start time
        const timeStr = appointment.startTime || appointment.start_time || appointment.time || '';
        if (!timeStr || typeof timeStr !== 'string') {
          return;
        }

        // Handle different time formats (HH:MM, HH:MM:SS, or ISO datetime)
        let timeParts: string[];
        if (timeStr.includes('T')) {
          // ISO format like "2025-12-09T10:00:00"
          const isoTime = timeStr.split('T')[1];
          if (!isoTime) return;
          timeParts = isoTime.split(':');
        } else {
          timeParts = timeStr.split(':');
        }

        if (!timeParts || timeParts.length < 2) {
          return;
        }
        const hours = Number(timeParts[0]);
        const minutes = Number(timeParts[1]);
        if (isNaN(hours) || isNaN(minutes)) {
          return;
        }

        const appointmentTime = new Date();
        appointmentTime.setHours(hours, minutes, 0, 0);

        // Check if appointment is in the 15-16 minute window
        if (appointmentTime >= in15Min && appointmentTime <= in16Min) {
          const title = appointment.title || 'Termin';
          const location = appointment.location ? ` (${appointment.location})` : '';

          // Trigger notification
          useNotificationsStore.getState().addNotification({
            type: 'meeting',
            title: 'Termin in 15 Minuten!',
            description: `${title}${location} um ${appointment.startTime || appointment.start_time}`,
            time: 'in 15 Min',
            priority: 'high',
            unread: true,
            actions: [
              { label: 'Zum Kalender', action: 'view_calendar', style: 'primary' }
            ]
          });

          // Mark as notified
          this.tracker.notifiedAppointments.add(appointment.id);
          this.saveTracker();

          console.log(`📅 Termin-Erinnerung: ${title} in 15 Minuten`);
        }
      });

      // Clean up old notified appointments (older than 2 hours)
      this.cleanupNotifiedAppointments();

    } catch (error) {
      console.error('❌ Error checking appointment reminders:', error);
    }
  }

  /**
   * Clean up notified appointments older than 2 hours
   * This prevents the Set from growing indefinitely
   */
  private cleanupNotifiedAppointments(): void {
    // For now, clear all if more than 100 entries
    // In production, you'd want to track timestamps and clean based on time
    if (this.tracker.notifiedAppointments.size > 100) {
      this.tracker.notifiedAppointments.clear();
      this.saveTracker();
    }
  }

  /**
   * Manually trigger a test notification
   * Useful for testing the notification system
   */
  testNotification(): void {
    useNotificationsStore.getState().addNotification({
      type: 'system',
      title: 'Test-Benachrichtigung',
      description: 'Das Benachrichtigungssystem funktioniert!',
      time: 'jetzt',
      priority: 'normal',
      unread: true
    });
    console.log('🔔 Test notification triggered');
  }

  /**
   * Reset notification tracker
   * Useful for testing or debugging
   */
  resetTracker(): void {
    this.tracker = {
      lastEmailCheck: null,
      lastCandidateCheck: null,
      notifiedAppointments: new Set<string>()
    };
    this.saveTracker();
    console.log('🔄 Notification tracker reset');
  }
}

// Export singleton instance
export const notificationsService = new NotificationsService();
export default notificationsService;
